import clsx from 'clsx'
import first from 'lodash/first'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import React, { FC, MutableRefObject, PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react'

import SvgAngleLeft from '@Assets/SVG/AngleLeft'
import { useIntersectingChildrenObserver } from '@Globals/Hooks/IntersectingChildren'
import { useTranslation } from '@Globals/Translations'
import { CapitalizeFirstLetter } from '@Shared/Utilities/StringHelpers/CapitalizeFirstLetter'

import styles from './TableOfContent.module.scss'

export type TableOfContentItem = {
  bullet: string
  id: string
  title: string
  type: string
}
export interface TableOfContentProps {
  tableOfContents: TableOfContentItem[]
  wrapperRef: MutableRefObject<HTMLDivElement | null>
}

export const TableOfContent: FC<PropsWithChildren<TableOfContentProps>> = ({ tableOfContents, wrapperRef }) => {
  const { t } = useTranslation()
  const [fixed, setFixed] = useState(false)
  const doBlur = useRef(true)
  const [expand, setExpand] = useState(false)
  const [animate, setAnimate] = useState(false)
  const rect = useRef<{ height: number, top: number } | null>(null)
  const placeHolderRef = useRef<HTMLDivElement | null>(null)
  const ref = useRef<HTMLDivElement | null>(null)
  const intersectingChildren = useIntersectingChildrenObserver(wrapperRef, { attributeName: 'id', rootMargin: '0px 0px 0px 0px' })
  const [activeIDs, setActiveIDs] = useState<string[]>(first(tableOfContents) ? [`title-${first(tableOfContents)!.id}`] : [])

  const scrollToID = useCallback((hashValue: string) => {
    const element = document.getElementById(hashValue)
    if (element) {
      setActiveIDs([hashValue])
      const headerOffset = 50
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.scrollY - headerOffset
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
    }
  }, [])

  const scrollFn = useCallback(() => {
    if (ref.current && (!rect.current?.height || !rect.current?.top)) {
      const boundingClientRect = ref.current?.getBoundingClientRect()
      rect.current = { height: +`${boundingClientRect.height}`, top: +`${window.scrollY + boundingClientRect.top}` }
      return
    }
    if (rect.current?.top && rect.current?.height && wrapperRef.current) {
      const isScrollYPassedTable = rect.current!.top + rect.current!.height < window.scrollY
      const isScrollYPassedWrapper = wrapperRef.current?.clientHeight < window.scrollY + window.innerHeight - (wrapperRef.current?.offsetTop || 0) - 75
      const isFixedPosition = isScrollYPassedTable && !isScrollYPassedWrapper
      setAnimate(fixed === isFixedPosition)
      setFixed(isFixedPosition)
    }
  }, [wrapperRef, fixed])

  useEffect(() => {
    scrollFn()
    window.addEventListener('scroll', scrollFn)
    return () => window.removeEventListener('scroll', scrollFn)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const keys: string[] = Object.keys(intersectingChildren).filter(key => key.startsWith('title-') && intersectingChildren[key])
    if (!isEmpty(keys)) {
      setActiveIDs([first(keys) as string])
    }
  }, [intersectingChildren])

  return (
    <>
      <div
        className={clsx(styles.tableOfContentPlaceholder, fixed && styles.fixed)}
        id="article-table-of-content-placeholder"
        ref={placeHolderRef}
        style={fixed ? { height: `${rect.current?.height}px` } : undefined}
      />
      <div
        className={clsx(
          styles.tableOfContent,
          rect.current?.height && fixed && styles.fixed,
          (!fixed || expand || !rect.current?.height) && styles.expanded,
          animate && styles.animate,
        )}
        id="article-table-of-content"
        onBlur={() => {
          setTimeout(() => {
            if (doBlur) {
              setExpand(false)
            }
          }, 100)
        }}
        ref={ref}
      >
        <button
          className={styles.header}
          onClick={() => {
            if (fixed) {
              doBlur.current = true
              setAnimate(true)
              setExpand(!expand)
            }
          }}
          type="button"
        >
          {t('common:TABLE_OF_CONTENT', 'Table of contents')}
          {fixed && <span className={clsx(styles.arrow, expand && styles.up)}><SvgAngleLeft /></span>}
        </button>
        <div className={styles.content}>
          {map(tableOfContents, (content, i) => (
            <span
              key={`TableOfContent_${content.id}_${i}`}
              className={clsx(styles.contentItem, activeIDs.includes(`title-${content.id}`) && styles.active)}
              onClick={() => {
                doBlur.current = false
                scrollToID(`title-${content.id}`)
                setExpand(false)
              }}
            >
              <span className={clsx(styles.title, styles[content.type])}>
                <span className={styles.bullet}>{`${content.bullet} `}</span>
                <span>{` ${CapitalizeFirstLetter(content.title)}`}</span>
              </span>
            </span>
          ))}
        </div>
      </div>
    </>
  )
}

